(() => {
  const FAMILY_FIXED = 'fixed';
  const FAMILY_UNLIMITED = 'daily_unlimited';

  const familyFor = (record) => record?.planFamily === FAMILY_UNLIMITED ? FAMILY_UNLIMITED : FAMILY_FIXED;
  const finiteOrInfinity = (value) => Number.isFinite(Number(value)) ? Number(value) : Number.POSITIVE_INFINITY;

  const dataGbFromTitle = (title) => {
    const match = String(title || '').match(/([\d.]+)\s*(MB|GB)/iu);
    if (!match) return Number.POSITIVE_INFINITY;
    const amount = Number(match[1]);
    return match[2].toUpperCase() === 'MB' ? amount / 1000 : amount;
  };

  const validityDaysFromTitle = (title) => {
    const match = String(title || '').match(/(\d+)\s*days?\s*$/iu);
    return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
  };

  const sortMetrics = ({ variant, record, index }) => {
    const title = String(variant?.title || '');
    const family = familyFor(record || (title.includes('Unlimited') ? { planFamily: FAMILY_UNLIMITED } : null));
    return {
      familyRank: family === FAMILY_FIXED ? 0 : 1,
      primary: family === FAMILY_FIXED
        ? finiteOrInfinity(record?.dataGb ?? dataGbFromTitle(title))
        : finiteOrInfinity(record?.validityDays ?? validityDaysFromTitle(title)),
      secondary: family === FAMILY_FIXED
        ? finiteOrInfinity(record?.validityDays ?? validityDaysFromTitle(title))
        : finiteOrInfinity(record?.dailyHighSpeedGb ?? dataGbFromTitle(title)),
      price: finiteOrInfinity(variant?.price),
      index,
    };
  };

  const comparePlanRows = (left, right) => {
    const a = sortMetrics(left);
    const b = sortMetrics(right);
    return a.familyRank - b.familyRank
      || a.primary - b.primary
      || a.secondary - b.secondary
      || a.price - b.price
      || a.index - b.index;
  };

  const sortPlanOptions = (variantSelects, variants, records) => {
    if (!variantSelects || !Array.isArray(variants)) return;
    const byTitle = new Map(variants.map((variant) => [String(variant.title || ''), variant]));
    const inputs = [...variantSelects.querySelectorAll('input[type="radio"]')];
    const parents = new Set(inputs.map((input) => input.parentElement).filter(Boolean));

    parents.forEach((parent) => {
      inputs
        .filter((input) => input.parentElement === parent)
        .map((input, index) => {
          const variant = byTitle.get(String(input.value || ''));
          return {
            input,
            label: input.nextElementSibling?.tagName === 'LABEL' ? input.nextElementSibling : null,
            variant,
            record: records[String(variant?.sku || '')],
            index,
          };
        })
        .sort(comparePlanRows)
        .forEach(({ input, label }) => {
          parent.append(input);
          if (label) parent.append(label);
        });
    });
  };

  const selectFirstPlanOnBareProductUrl = (variantSelects) => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('variant') || variantSelects.querySelector('roamuno-plan-family')) return;
    const first = [...variantSelects.querySelectorAll('input[type="radio"]')]
      .find((input) => !input.hidden && !input.disabled && !input.classList.contains('disabled'));
    if (!first || first.checked) return;

    Promise.all([
      customElements.whenDefined('variant-selects'),
      customElements.whenDefined('product-info'),
    ]).then(() => {
      requestAnimationFrame(() => first.click());
    });
  };

  class RoamunoPlanFamily extends HTMLElement {
    connectedCallback() {
      this.variantSelects = this.closest('variant-selects');
      this.variantsNode = document.querySelector('[data-product-variants]');
      if (!this.variantSelects || !this.variantsNode) return;

      try {
        this.variants = JSON.parse(this.variantsNode.textContent || '[]');
      } catch (_) {
        return;
      }

      this.records = window.ROAMUNO_PLAN_DATA?.bySku || {};
      this.byTitle = new Map(this.variants.map((variant) => [String(variant.title || ''), variant]));
      this.familyByTitle = new Map(this.variants.map((variant) => [
        String(variant.title || ''),
        familyFor(this.records[String(variant.sku || '')]),
      ]));
      sortPlanOptions(this.variantSelects, this.variants, this.records);
      this.families = new Set(this.familyByTitle.values());
      if (!this.families.has(FAMILY_FIXED) || !this.families.has(FAMILY_UNLIMITED)) return;

      this.hidden = false;
      this.tabs = [...this.querySelectorAll('[role="tab"][data-plan-family]')];
      this.tabs.forEach((tab) => tab.addEventListener('click', () => this.selectFamily(tab.dataset.planFamily, true)));
      this.addEventListener('keydown', (event) => this.onKeydown(event));

      const url = new URL(window.location.href);
      const requested = url.searchParams.get('plan_type');
      const selected = this.selectedFamily();
      const wanted = url.searchParams.has('variant') ? selected
        : requested === 'unlimited' ? FAMILY_UNLIMITED : requested === 'fixed' ? FAMILY_FIXED : selected;
      this.selectFamily(wanted, wanted !== selected || !url.searchParams.has('variant'), !url.searchParams.has('variant'));
    }

    optionInputs() {
      return [...this.variantSelects.querySelectorAll('input[type="radio"]')];
    }

    selectedFamily() {
      const selected = this.optionInputs().find((input) => input.checked);
      return this.familyByTitle.get(String(selected?.value || '')) || FAMILY_FIXED;
    }

    setUrl(family) {
      const url = new URL(window.location.href);
      url.searchParams.set('plan_type', family === FAMILY_UNLIMITED ? 'unlimited' : 'fixed');
      window.history.replaceState({}, '', url);
    }

    selectFamily(family, activateVariant, preferFirst = false) {
      if (!this.families.has(family)) family = FAMILY_FIXED;
      this.tabs.forEach((tab) => {
        const selected = tab.dataset.planFamily === family;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected) this.variantSelects.querySelector('[role="tabpanel"]')?.setAttribute('aria-labelledby', tab.id);
      });

      const matching = [];
      this.optionInputs().forEach((input) => {
        const visible = this.familyByTitle.get(String(input.value || '')) === family;
        input.hidden = !visible;
        const label = input.nextElementSibling;
        if (label?.tagName === 'LABEL') label.hidden = !visible;
        if (visible && !input.classList.contains('disabled')) matching.push(input);
      });

      this.setUrl(family);

      const checkedMatches = matching.some((input) => input.checked);
      if (activateVariant && matching[0] && (!checkedMatches || (preferFirst && !matching[0].checked))) matching[0].click();
    }

    onKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? this.tabs.length - 1
          : event.key === 'ArrowRight'
            ? (selectedIndex + 1) % this.tabs.length
            : (selectedIndex - 1 + this.tabs.length) % this.tabs.length;
      event.preventDefault();
      this.tabs[nextIndex].focus();
      this.tabs[nextIndex].click();
    }
  }

  RoamunoPlanFamily.comparePlanRows = comparePlanRows;
  if (!customElements.get('roamuno-plan-family')) customElements.define('roamuno-plan-family', RoamunoPlanFamily);

  const syncStandalonePlanOptions = () => {
    const picker = document.querySelector('main variant-selects');
    const variantsNode = document.querySelector('[data-product-variants]');
    if (!picker || !variantsNode) return;
    try {
      sortPlanOptions(picker, JSON.parse(variantsNode.textContent || '[]'), window.ROAMUNO_PLAN_DATA?.bySku || {});
      selectFirstPlanOnBareProductUrl(picker);
    } catch (_) {
      // Shopify's original option order remains usable when embedded data is unavailable.
    }
  };

  syncStandalonePlanOptions();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(syncStandalonePlanOptions));
  }
})();
